import { AuthenticatedUser } from "@gettactic/api";
import {
  MARIN_ROUTES,
  SETTINGS_ROUTES
} from "@gettactic/defines/src/web-routes";
import { buildUrl } from "@gettactic/helpers";
import { canAccessAnalytics } from "@/features/reporting/lib/access";

export const SEARCH_DEBOUNCE_MS = 300;
export const LIVE_MODE_UPDATE_INTERVAL_MS = 60000; // 1 minute
export const RESOURCE_INFO_MODAL_DELAY_MS = 800;
export const INPUT_FOCUS_DELAY_MS = 100;
export const TESSA_MESSAGE_DELAY_MS = 100;

export type Setting = {
  id: string;
  type: "action" | "setting";
  name: string;
  description?: string;
  url?: string;
  isAdmin?: boolean;
  isAvailable?: (authenticatedUser: AuthenticatedUser) => boolean;
};

export const QUICK_ACTIONS: Setting[] = [
  {
    id: "s-reserve-desk",
    type: "action",
    name: "Quick reserve desk",
    isAvailable: (authenticatedUser: AuthenticatedUser) =>
      authenticatedUser?.organization?.desk_bookings_enabled ?? false
  },
  {
    id: "s-reserve-room",
    type: "action",
    name: "Quick reserve meeting room",
    isAvailable: (authenticatedUser: AuthenticatedUser) =>
      authenticatedUser?.organization?.meeting_room_bookings_enabled ?? false
  }
];

export const SETTINGS: Setting[] = [
  {
    id: "s-reports-boards",
    type: "setting",
    url: `${buildUrl(MARIN_ROUTES.analytics)}#tab-boards`,
    name: "Analytics · Boards",
    description: "Occupancy, requests, visitors and more at a glance",
    isAvailable: canAccessAnalytics
  },
  {
    id: "s-reports-explore",
    type: "setting",
    url: `${buildUrl(MARIN_ROUTES.analytics)}#tab-explore`,
    name: "Analytics · Explore",
    description: "Ask a question, drill into the answer",
    isAvailable: canAccessAnalytics
  },
  {
    id: "s-reports-insights",
    type: "setting",
    url: `${buildUrl(MARIN_ROUTES.analytics)}#tab-insights`,
    name: "Analytics · Insights",
    description:
      "Which amenities get a desk booked, where requests stall, the team day",
    isAvailable: canAccessAnalytics
  },
  {
    id: "s-reports-definitions",
    type: "setting",
    url: `${buildUrl(MARIN_ROUTES.analytics)}#tab-definitions`,
    name: "Analytics · Definitions",
    description: "What every number counts and leaves out",
    isAvailable: canAccessAnalytics
  },
  {
    id: "s-profile-settings",
    type: "setting",
    url: buildUrl(SETTINGS_ROUTES.profile),
    name: "Profile Settings",
    description: "Manage your profile"
  },
  {
    id: "s-organization-settings",
    type: "setting",
    isAdmin: true,
    url: buildUrl(SETTINGS_ROUTES.organization),
    name: "Organization Settings",
    description: "General organization settings and defaults"
  },
  {
    id: "s-facilities",
    type: "setting",
    url: buildUrl(SETTINGS_ROUTES.facilities),
    name: "Facilities",
    description:
      "Manage divisions, buildings, floors, zones, workspaces, and parking."
  },
  {
    id: "s-amenities",
    type: "setting",
    url: buildUrl(SETTINGS_ROUTES.amenities),
    name: "Amenities",
    description: "Create & edit amenities."
  },
  {
    id: "s-users-teams",
    type: "setting",
    isAdmin: true,
    url: buildUrl(SETTINGS_ROUTES.usersAndTeamsUsers),
    name: "Users & teams",
    description: "Manage all users and teams within your organization"
  },
  {
    id: "s-integrations",
    type: "setting",
    url: buildUrl(SETTINGS_ROUTES.integrations),
    name: "Integrations",
    description:
      "Set up integrations & third party apps with your Tactic account."
  }
];
